import { GAME_NAME } from "@gaoya/shared";
import { BGM_VOLUME_WECHAT, sfxVolume } from "../audio/levels.js";
import { getAudioPrefs } from "../audio/prefs.js";
import { SFX } from "../audio/sfx.js";
import { pcmWavToAudioBuffer } from "../audio/wav.js";
import { HTTP_BASE, WS_URL, WS_URL_PLAIN } from "../config.js";
import type {
  GameCanvas,
  HttpJson,
  Platform,
  PointerEvent,
  SafeInset,
  SharePayload,
  Size,
  SocketHandle,
  SocketHandlers,
  UiRect,
  WxUserInfo,
} from "./types.js";

export function isWechatRuntime(): boolean {
  try {
    return typeof wx !== "undefined" && typeof wx.createCanvas === "function";
  } catch {
    return false;
  }
}

type NativeUserInfoButton = {
  show(): void;
  hide(): void;
  destroy(): void;
  onTap(cb: (res: { errMsg?: string; userInfo?: { nickName?: string; avatarUrl?: string } }) => void): void;
};

type WxApi = NonNullable<typeof wx>;

const BGM_VOLUME = BGM_VOLUME_WECHAT;
const SFX_VOLUME = sfxVolume(BGM_VOLUME);
const DEFAULT_SHARE: SharePayload = {
  title: `${GAME_NAME} · 成语接龙对决`,
  imagePath: "assets/icon.png",
};
let wechatBgm: ReturnType<WxApi["createInnerAudioContext"]> | null = null;
let wechatBgmSrc = "";
let wechatBgmBound = false;
/** 仅在 onPlay 置位。启动时调用 play() 失败不能把它当「正在播」，否则 iPhone 首次点按会被跳过。 */
let wechatBgmPlaying = false;
let wechatBgmPos = 0;
let audioUnlocked = false;
let innerAudioOptionSet = false;
let webAudio: AudioContext | null = null;
const sfxBuffers = new Map<string, AudioBuffer>();
const sfxLoading = new Set<string>();
let menuShare: SharePayload = { ...DEFAULT_SHARE };
const wechatSfx = new Map<string, ReturnType<WxApi["createInnerAudioContext"]>>();

function ensureInnerAudioOption(api: WxApi): void {
  if (innerAudioOptionSet) return;
  innerAudioOptionSet = true;
  try {
    api.setInnerAudioOption?.({
      mixWithOther: true,
      obeyMuteSwitch: false,
      speakerOn: true,
    });
  } catch {
    /* ignore */
  }
}

function ensureWebAudio(api: WxApi): AudioContext | null {
  if (webAudio) return webAudio;
  try {
    webAudio = api.createWebAudioContext?.() ?? null;
  } catch {
    webAudio = null;
  }
  return webAudio;
}

function resumeWebAudio(api: WxApi): void {
  const ctx = ensureWebAudio(api);
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") void ctx.resume();
  } catch {
    /* ignore */
  }
}

function decodeSfxBuffer(ctx: AudioContext, data: ArrayBuffer): Promise<AudioBuffer | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (buf: AudioBuffer | null): void => {
      if (settled) return;
      settled = true;
      resolve(buf);
    };
    try {
      const ret: unknown = ctx.decodeAudioData(
        data,
        (buf) => done(buf),
        () => done(null),
      );
      if (ret && typeof (ret as Promise<AudioBuffer>).then === "function") {
        (ret as Promise<AudioBuffer>).then((buf) => done(buf), () => done(null));
      }
    } catch {
      done(null);
    }
  });
}

function arrayBufferOf(data: ArrayBuffer | string | undefined): ArrayBuffer | null {
  return data instanceof ArrayBuffer ? data : null;
}

function bufferFromWavBytes(ctx: AudioContext, data: ArrayBuffer): AudioBuffer | null {
  const fromPcm = pcmWavToAudioBuffer(ctx, data);
  if (fromPcm) return fromPcm;
  return null;
}

function loadSfxBufferSync(api: WxApi, src: string): AudioBuffer | null {
  const hit = sfxBuffers.get(src);
  if (hit) return hit;
  const ctx = ensureWebAudio(api);
  const fs = api.getFileSystemManager?.();
  if (!ctx || !fs?.readFileSync) return null;
  try {
    const data = arrayBufferOf(fs.readFileSync(src));
    if (!data) return null;
    const buf = bufferFromWavBytes(ctx, data);
    if (buf) sfxBuffers.set(src, buf);
    return buf;
  } catch {
    return null;
  }
}

function loadSfxBuffer(api: WxApi, src: string): void {
  if (sfxBuffers.has(src) || sfxLoading.has(src)) return;
  const synced = loadSfxBufferSync(api, src);
  if (synced) return;
  const ctx = ensureWebAudio(api);
  const fs = api.getFileSystemManager?.();
  if (!ctx || !fs) return;
  sfxLoading.add(src);
  fs.readFile({
    filePath: src,
    success: (res) => {
      const data = arrayBufferOf(res.data);
      if (!data) {
        sfxLoading.delete(src);
        return;
      }
      const parsed = bufferFromWavBytes(ctx, data);
      if (parsed) {
        sfxLoading.delete(src);
        sfxBuffers.set(src, parsed);
        return;
      }
      void decodeSfxBuffer(ctx, data).then((buf) => {
        sfxLoading.delete(src);
        if (buf) sfxBuffers.set(src, buf);
      });
    },
    fail: () => {
      sfxLoading.delete(src);
    },
  });
}

function preloadSfx(api: WxApi): void {
  for (const src of Object.values(SFX)) loadSfxBuffer(api, src);
}

function playWebSfx(api: WxApi, src: string): boolean {
  const ctx = ensureWebAudio(api);
  const buf = sfxBuffers.get(src);
  if (!ctx || !buf) return false;
  try {
    const node = ctx.createBufferSource();
    node.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = SFX_VOLUME;
    node.connect(gain);
    gain.connect(ctx.destination);
    node.start(0);
    return true;
  } catch {
    return false;
  }
}

function unlockWechatAudio(api: WxApi): void {
  ensureInnerAudioOption(api);
  const first = !audioUnlocked;
  audioUnlocked = true;
  resumeWebAudio(api);
  preloadSfx(api);
  if (first) startWechatBgm(api, wechatBgmSrc || "assets/bgm.mp3");
  else resumeWechatBgm();
}

function rememberBgmPos(): void {
  const t = wechatBgm?.currentTime;
  if (typeof t === "number" && Number.isFinite(t) && t >= 0) wechatBgmPos = t;
}

function pauseWechatBgm(): void {
  if (!wechatBgm) return;
  rememberBgmPos();
  wechatBgmPlaying = false;
  try {
    wechatBgm.pause();
  } catch {
    /* ignore */
  }
}

function resumeWechatBgm(): void {
  if (!wechatBgm || !audioUnlocked) return;
  if (getAudioPrefs().bgmMuted) {
    pauseWechatBgm();
    return;
  }
  if (wechatBgm.paused === false) return;
  try {
    if (wechatBgmPos > 0.05) wechatBgm.seek?.(wechatBgmPos);
    wechatBgm.play();
  } catch {
    wechatBgmPlaying = false;
  }
}

function startWechatBgm(api: WxApi, src: string): void {
  ensureInnerAudioOption(api);
  const nextSrc = src || wechatBgmSrc;
  if (!audioUnlocked) {
    wechatBgmSrc = nextSrc;
    if (!wechatBgmBound) bindBgmLifecycle(api);
    return;
  }
  if (!wechatBgm) {
    wechatBgmSrc = nextSrc;
    // iPhone 必须在用户点按的回调里创建 InnerAudioContext，启动阶段创建再 play 会静音。
    wechatBgm = api.createInnerAudioContext();
    wechatBgm.autoplay = false;
    wechatBgm.loop = true;
    wechatBgm.obeyMuteSwitch = false;
    wechatBgm.volume = BGM_VOLUME;
    wechatBgm.src = wechatBgmSrc;
    wechatBgm.onError((err) => {
      wechatBgmPlaying = false;
      console.log("[gaoya] bgm error", err.errMsg ?? "");
    });
    wechatBgm.onPlay?.(() => {
      wechatBgmPlaying = true;
    });
    wechatBgm.onCanplay?.(() => {
      if (audioUnlocked && !getAudioPrefs().bgmMuted && wechatBgm?.paused !== false && !wechatBgmPlaying) {
        try {
          wechatBgm?.play();
        } catch {
          /* ignore */
        }
      }
    });
    wechatBgm.onPause?.(() => {
      rememberBgmPos();
      wechatBgmPlaying = false;
    });
    wechatBgm.onStop?.(() => {
      wechatBgmPlaying = false;
      wechatBgmPos = 0;
    });
    wechatBgm.onTimeUpdate?.(() => rememberBgmPos());
    wechatBgm.onEnded?.(() => {
      if (getAudioPrefs().bgmMuted) return;
      wechatBgmPlaying = false;
      wechatBgmPos = 0;
      resumeWechatBgm();
    });
  } else if (nextSrc && wechatBgmSrc !== nextSrc) {
    wechatBgmSrc = nextSrc;
    wechatBgmPos = 0;
    wechatBgmPlaying = false;
    wechatBgm.src = nextSrc;
  } else {
    wechatBgmSrc = nextSrc;
  }
  if (!wechatBgmBound) bindBgmLifecycle(api);
  resumeWechatBgm();
}

function bindBgmLifecycle(api: WxApi): void {
  wechatBgmBound = true;
  api.onShow?.(() => {
    if (audioUnlocked) resumeWechatBgm();
  });
  api.onHide?.(() => {
    pauseWechatBgm();
  });
  api.onAudioInterruptionBegin?.(() => {
    pauseWechatBgm();
  });
  api.onAudioInterruptionEnd?.(() => {
    if (audioUnlocked) resumeWechatBgm();
  });
}

function assignMenuShare(payload: SharePayload): void {
  const next: SharePayload = { title: payload.title || DEFAULT_SHARE.title };
  if (payload.query) next.query = payload.query;
  const image = payload.imagePath || DEFAULT_SHARE.imagePath;
  if (image) next.imagePath = image;
  menuShare = next;
}

function toWxShare(payload: SharePayload): { title?: string; query?: string; imageUrl?: string } {
  const opts: { title?: string; query?: string; imageUrl?: string } = {
    title: payload.title || DEFAULT_SHARE.title,
  };
  if (payload.query) opts.query = payload.query;
  if (payload.imagePath) opts.imageUrl = payload.imagePath;
  else if (DEFAULT_SHARE.imagePath) opts.imageUrl = DEFAULT_SHARE.imagePath;
  return opts;
}

function enableShareMenu(api: WxApi): void {
  try {
    api.showShareMenu?.({
      withShareTicket: true,
      menus: ["shareAppMessage", "shareTimeline"],
    });
  } catch {
    try {
      api.showShareMenu?.({ menus: ["shareAppMessage"] });
    } catch {
      /* ignore */
    }
  }
  try {
    api.onShareAppMessage?.(() => toWxShare(menuShare));
  } catch {
    /* ignore */
  }
  try {
    api.onShareTimeline?.(() => toWxShare(menuShare));
  } catch {
    /* ignore */
  }
}

function readQuery(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) out[k] = s;
  }
  return out;
}

function playInnerSfx(api: WxApi, src: string): void {
  let audio = wechatSfx.get(src);
  if (!audio) {
    audio = api.createInnerAudioContext();
    audio.autoplay = false;
    audio.loop = false;
    audio.obeyMuteSwitch = false;
    audio.volume = SFX_VOLUME;
    audio.src = src;
    audio.onError((err) => console.log("[gaoya] sfx error", err.errMsg ?? src));
    wechatSfx.set(src, audio);
  }
  try {
    // iPhone 上 stop() 后立刻 play() 经常被丢掉；未在播时直接 play，在播则 seek 到开头。
    if (audio.paused === false) audio.seek?.(0);
    audio.play();
  } catch {
    /* ignore */
  }
}

function playWechatSfx(api: WxApi, src: string): void {
  if (getAudioPrefs().sfxMuted) return;
  ensureInnerAudioOption(api);
  if (audioUnlocked) resumeWebAudio(api);
  loadSfxBufferSync(api, src);
  loadSfxBuffer(api, src);
  if (playWebSfx(api, src)) return;
  playInnerSfx(api, src);
}

let userInfoBtn: NativeUserInfoButton | null = null;
let lastBtnKey = "";
let tapHandler: (info: WxUserInfo | null) => void = () => {};
let privacyResolve: ((args: { event: string; buttonId?: string }) => void) | null = null;
let privacyAgreeIntent = false;
let onPrivacyPromptCb: (() => void) | null = null;
type OpenDataContext = {
  canvas: { width: number; height: number };
  postMessage(data: unknown): void;
};
let openData: OpenDataContext | null = null;

function destroyUserInfoButton(): void {
  if (!userInfoBtn) return;
  try {
    userInfoBtn.destroy();
  } catch {
    /* ignore */
  }
  userInfoBtn = null;
  lastBtnKey = "";
}

export function createWechatPlatform(): Platform {
  if (typeof wx === "undefined" || typeof wx.createCanvas !== "function") {
    throw new Error("wx.createCanvas unavailable");
  }
  const api = wx;
  ensureInnerAudioOption(api);
  enableShareMenu(api);
  if (api.onNeedPrivacyAuthorization) {
    api.onNeedPrivacyAuthorization((resolve) => {
      if (privacyAgreeIntent) {
        privacyResolve = null;
        try {
          resolve({ event: "agree" });
        } catch {
          /* ignore */
        }
        return;
      }
      privacyResolve = resolve;
      try {
        resolve({ event: "exposureAuthorization" });
      } catch {
        /* ignore */
      }
      onPrivacyPromptCb?.();
    });
  }
  const canvas = api.createCanvas();
  const info = api.getSystemInfoSync();
  // 真机 2.5–3x 屏若按满 DPR 开画布，2D 填充量过大；网页端 GPU 更强所以不明显。
  const dpr = Math.min(2, Math.max(1, info.pixelRatio || 1));
  const logical: Size = { w: info.windowWidth, h: info.windowHeight };
  canvas.width = Math.floor(logical.w * dpr);
  canvas.height = Math.floor(logical.h * dpr);

  const cachedInset = readSafeInset(api, info, logical);

  const raf =
    typeof canvas.requestAnimationFrame === "function"
      ? (cb: (t: number) => void) => canvas.requestAnimationFrame!(cb)
      : (cb: (t: number) => void) => requestAnimationFrame(cb);

  const fireTouch = (kind: PointerEvent["kind"], e: { changedTouches: { identifier: number; clientX: number; clientY: number; x?: number; y?: number }[] }, cb: (ev: PointerEvent) => void) => {
    const t = e.changedTouches[0];
    if (!t) return;
    const x = t.x ?? t.clientX;
    const y = t.y ?? t.clientY;
    cb({ kind, x, y, id: t.identifier });
  };

  const platform: Platform = {
    kind: "wechat",
    canvas: canvas as unknown as GameCanvas,
    logical,
    dpr,
    now: () => Date.now(),
    raf,
    onResize(cb) {
      /* 小游戏旋转已锁定竖屏；尺寸取启动时 window */
      cb({ w: logical.w, h: logical.h });
    },
    onPointer(cb) {
      api.onTouchStart((e) => {
        unlockWechatAudio(api);
        fireTouch("down", e, cb);
      });
      api.onTouchMove((e) => fireTouch("move", e, cb));
      api.onTouchEnd((e) => fireTouch("up", e, cb));
      api.onTouchCancel((e) => fireTouch("cancel", e, cb));
      api.onShow?.(() => {
        cb({ kind: "cancel", x: 0, y: 0, id: -1 });
      });
    },
    onKey() {
      /* 小游戏无实体键盘 */
    },
    vibrateShort() {
      if (getAudioPrefs().vibrateMuted) return;
      try {
        api.vibrateShort({ type: "light" });
      } catch {
        /* ignore */
      }
    },
    connectSocket(url, handlers: SocketHandlers): SocketHandle {
      let closed = false;
      const emitClose = () => {
        if (closed) return;
        closed = true;
        handlers.onClose();
      };
      const task = api.connectSocket({
        url,
        fail: (err) => {
          handlers.onError(err.errMsg ?? "connect fail");
          // fail 可能同步触发；推迟 close，避免 open() 把已作废的 task 又赋回 handle
          setTimeout(emitClose, 0);
        },
      });
      task.onOpen(() => handlers.onOpen());
      task.onMessage((res) => {
        if (typeof res.data === "string") handlers.onMessage(res.data);
      });
      task.onClose(() => emitClose());
      task.onError((err) => handlers.onError(err.errMsg ?? "socket error"));
      return {
        send(data) {
          task.send({ data });
        },
        close() {
          try {
            task.close();
          } catch {
            /* ignore */
          }
        },
      };
    },
    loadImage(src) {
      return new Promise((resolve) => {
        const img = api.createImage();
        img.onload = () => resolve(img as unknown as CanvasImageSource);
        img.onerror = () => resolve(null);
        img.src = src;
      });
    },
    getStorage(key) {
      try {
        const v = api.getStorageSync(key);
        return typeof v === "string" ? v : v != null ? String(v) : null;
      } catch {
        return null;
      }
    },
    setStorage(key, value) {
      try {
        api.setStorageSync(key, value);
      } catch {
        /* ignore */
      }
    },
    httpJson(method, url, body): Promise<HttpJson> {
      return new Promise((resolve) => {
        api.request({
          url,
          method,
          data: body ?? {},
          header: { "Content-Type": "application/json" },
          success: (res) => resolve({ ok: res.statusCode >= 200 && res.statusCode < 300, status: res.statusCode, body: res.data }),
          fail: () => resolve({ ok: false, status: 0, body: null }),
        });
      });
    },
    getLoginCode() {
      return new Promise((resolve) => {
        if (!api.login) {
          resolve(null);
          return;
        }
        api.login({
          success: (res) => resolve(res.code || null),
          fail: () => resolve(null),
        });
      });
    },
    fetchAuthorizedUserInfo() {
      return new Promise((resolve) => {
        if (!api.getSetting || !api.getUserInfo) {
          resolve(null);
          return;
        }
        api.getSetting({
          success: (res) => {
            if (!res.authSetting || res.authSetting["scope.userInfo"] !== true) {
              resolve(null);
              return;
            }
            api.getUserInfo!({
              success: (r) => {
                const nick = (r.userInfo && r.userInfo.nickName ? r.userInfo.nickName : "").trim();
                if (!nick) {
                  resolve(null);
                  return;
                }
                resolve({
                  nickname: nick.slice(0, 16),
                  avatar: (r.userInfo && r.userInfo.avatarUrl) || "",
                });
              },
              fail: () => resolve(null),
            });
          },
          fail: () => resolve(null),
        });
      });
    },
    syncUserInfoButton(rect, onResult) {
      tapHandler = onResult;
      const key = [Math.round(rect.x), Math.round(rect.y), Math.round(rect.w), Math.round(rect.h)].join(",");
      if (userInfoBtn && lastBtnKey === key) return;
      destroyUserInfoButton();
      lastBtnKey = key;
      if (!api.createUserInfoButton) return;
      userInfoBtn = api.createUserInfoButton({
        type: "text",
        text: "",
        style: {
          left: rect.x,
          top: rect.y,
          width: rect.w,
          height: rect.h,
          lineHeight: rect.h,
          backgroundColor: "rgba(0,0,0,0)",
          color: "transparent",
          fontSize: 16,
          borderRadius: 8,
          borderWidth: 0,
          textAlign: "center",
        },
      });
      userInfoBtn.onTap((res) => {
        const nick = (res.userInfo && res.userInfo.nickName ? res.userInfo.nickName : "").trim();
        if (!nick) {
          tapHandler(null);
          return;
        }
        tapHandler({
          nickname: nick.slice(0, 16),
          avatar: (res.userInfo && res.userInfo.avatarUrl) || "",
        });
      });
    },
    hideUserInfoButton() {
      destroyUserInfoButton();
    },
    requirePrivacyAuthorize() {
      return new Promise((resolve) => {
        if (!api.requirePrivacyAuthorize) {
          resolve(true);
          return;
        }
        api.requirePrivacyAuthorize({
          success: () => resolve(true),
          fail: () => resolve(false),
        });
      });
    },
    openPrivacyContract() {
      try {
        api.openPrivacyContract?.({});
      } catch {
        /* ignore */
      }
    },
    resolvePrivacyAuthorization(agreed) {
      privacyAgreeIntent = agreed;
      const resolve = privacyResolve;
      privacyResolve = null;
      if (!resolve) return;
      try {
        resolve(agreed ? { event: "agree" } : { event: "disagree" });
      } catch {
        /* ignore */
      }
    },
    onPrivacyPrompt(cb) {
      onPrivacyPromptCb = cb;
    },
    publishWins(wins) {
      const incoming = Math.max(0, Math.floor(wins));
      const localRaw = api.getStorageSync("gaoya.wins");
      const local = Number(typeof localRaw === "string" || typeof localRaw === "number" ? localRaw : 0);
      const floor = Number.isFinite(local) ? Math.max(0, Math.floor(local)) : 0;
      const next = Math.max(incoming, floor);
      try {
        api.setStorageSync("gaoya.wins", String(next));
      } catch {
        /* ignore */
      }
      const push = (value: number): void => {
        if (!api.setUserCloudStorage) return;
        api.setUserCloudStorage({
          KVDataList: [{ key: "wins", value: String(value) }],
          success: () => {
            if (openData) openData.postMessage({ type: "refresh" });
          },
        });
      };
      if (!api.getUserCloudStorage) {
        push(next);
        return;
      }
      api.getUserCloudStorage({
        keyList: ["wins"],
        success: (res) => {
          let cloud = 0;
          const list = res.KVDataList ?? [];
          for (const kv of list) {
            if (kv.key === "wins") {
              const n = parseInt(kv.value, 10);
              if (Number.isFinite(n)) cloud = n;
            }
          }
          const value = Math.max(next, cloud);
          try {
            api.setStorageSync("gaoya.wins", String(value));
          } catch {
            /* ignore */
          }
          push(value);
        },
        fail: () => push(next),
      });
    },
    showFriendRank(rect: UiRect, mode?: "panel" | "strip", self?: string) {
      if (!openData && api.getOpenDataContext) {
        openData = api.getOpenDataContext();
      }
      if (!openData) return;
      const pw = Math.max(1, Math.round(rect.w * dpr));
      const ph = Math.max(1, Math.round(rect.h * dpr));
      if (openData.canvas.width !== pw || openData.canvas.height !== ph) {
        openData.canvas.width = pw;
        openData.canvas.height = ph;
      }
      openData.postMessage({ type: "show", mode: mode || "panel", self: self || "", width: rect.w, height: rect.h });
    },
    hideFriendRank() {
      if (openData) openData.postMessage({ type: "hide" });
    },
    getFriendRankCanvas() {
      if (!openData && api.getOpenDataContext) {
        openData = api.getOpenDataContext();
      }
      return openData ? (openData.canvas as unknown as CanvasImageSource) : null;
    },
    resolveWsUrl() {
      return { primary: WS_URL, fallback: WS_URL_PLAIN };
    },
    resolveHttpBase: () => HTTP_BASE,
    playLoopingBgm(src) {
      startWechatBgm(api, src);
    },
    playSfx(src) {
      playWechatSfx(api, src);
    },
    applyAudioPrefs() {
      if (getAudioPrefs().bgmMuted) pauseWechatBgm();
      else if (audioUnlocked) resumeWechatBgm();
    },
    safeInset() {
      return cachedInset;
    },
    copyText(text) {
      return new Promise((resolve) => {
        if (!api.setClipboardData) {
          resolve(false);
          return;
        }
        api.setClipboardData({
          data: text,
          success: () => resolve(true),
          fail: () => resolve(false),
        });
      });
    },
    setShareMenu(payload) {
      assignMenuShare(payload);
    },
    shareApp(payload) {
      assignMenuShare(payload);
      try {
        api.shareAppMessage?.(toWxShare(menuShare));
      } catch {
        /* ignore */
      }
    },
    getLaunchQuery() {
      try {
        return readQuery(api.getLaunchOptionsSync?.()?.query);
      } catch {
        return {};
      }
    },
    getLaunchScene() {
      try {
        const scene = api.getLaunchOptionsSync?.()?.scene;
        return typeof scene === "number" ? scene : null;
      } catch {
        return null;
      }
    },
    onAppShow(cb) {
      api.onShow?.((res) => cb(readQuery(res?.query), res?.scene));
    },
    onAppHide(cb) {
      api.onHide?.(() => cb());
    },
    createOffscreenCanvas(width, height) {
      try {
        const c = api.createCanvas();
        c.width = width;
        c.height = height;
        const ctx = c.getContext("2d");
        if (!ctx) return null;
        return { canvas: c as unknown as GameCanvas, ctx, width, height };
      } catch {
        return null;
      }
    },
    exportCanvasPng(handle) {
      return new Promise((resolve) => {
        const c = handle.canvas;
        if (!c.toTempFilePath) {
          resolve(null);
          return;
        }
        c.toTempFilePath({
          fileType: "png",
          success: (res) => resolve(res.tempFilePath),
          fail: () => resolve(null),
        });
      });
    },
    log: (...args) => console.log("[gaoya]", ...args),
  };
  return platform;
}

function readSafeInset(
  api: WxApi,
  info: ReturnType<WxApi["getSystemInfoSync"]>,
  logical: Size,
): SafeInset {
  try {
    const menu = api.getMenuButtonBoundingClientRect?.();
    const bar = info.statusBarHeight ?? 8;
    const fallbackLeft = Math.max(80, logical.w - 96);
    const area = info.safeArea;
    const bottom = Math.max(
      0,
      Math.min(48, area && typeof area.bottom === "number" ? logical.h - area.bottom : 0),
    );
    if (menu && menu.left > 0 && menu.width > 0) {
      return {
        top: Math.max(bar, menu.top),
        bottom,
        capsuleLeft: menu.left,
        capsuleTop: menu.top,
        capsuleHeight: Math.max(24, menu.height),
      };
    }
    return { top: bar, bottom, capsuleLeft: fallbackLeft, capsuleTop: bar, capsuleHeight: 32 };
  } catch {
    return { top: 8, bottom: 0, capsuleLeft: Math.max(80, logical.w - 96), capsuleTop: 8, capsuleHeight: 32 };
  }
}
